import {
  Dialog,
  DialogContent,
  Grid,
  Button,
  CircularProgress,
  Box,
  Typography,
  Chip,
  alpha,
} from "@mui/material";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getCountries, updateCountry } from "../../utils/country";
import { toast } from "react-toastify";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { InputBase } from "@mui/material";

// ---------------------------------------------------------------------------
// Motion wrappers
// ---------------------------------------------------------------------------
const MotionBox = motion(Box);
const MotionGrid = motion(Grid);

// ---------------------------------------------------------------------------
// Stagger helpers
// ---------------------------------------------------------------------------
const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.045, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.97 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 380, damping: 28 },
  },
};

const dialogVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.97 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
  exit: {
    opacity: 0,
    y: 16,
    scale: 0.98,
    transition: { duration: 0.18, ease: "easeIn" },
  },
};

// ---------------------------------------------------------------------------
// Country tile
// ---------------------------------------------------------------------------
const CountryTile = ({ country, selected, onClick }) => (
  <Grid item xs={6} sm={4}>
    <MotionBox
      variants={itemVariants}
      whileHover={{ scale: 1.03, transition: { duration: 0.18 } }}
      whileTap={{ scale: 0.97 }}
      onClick={() => onClick(country.id)}
      sx={{
        position: "relative",
        cursor: "pointer",
        borderRadius: "14px",
        border: "1.5px solid",
        borderColor: selected ? "primary.main" : "grey.200",
        bgcolor: selected ? alpha("#4F46E5", 0.06) : "#FAFAFA",
        p: "12px 14px",
        display: "flex",
        alignItems: "center",
        gap: 1.2,
        transition: "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
        boxShadow: selected
          ? "0 0 0 3px rgba(79,70,229,0.14)"
          : "0 1px 3px rgba(0,0,0,0.04)",
        "&:hover": {
          borderColor: selected ? "primary.main" : "grey.400",
          bgcolor: selected ? alpha("#4F46E5", 0.07) : "grey.50",
          boxShadow: selected
            ? "0 0 0 3px rgba(79,70,229,0.18)"
            : "0 2px 8px rgba(0,0,0,0.07)",
        },
        userSelect: "none",
      }}
    >
      {/* Flag emoji or icon placeholder */}
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          bgcolor: selected ? "primary.main" : "grey.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background-color 0.2s",
        }}
      >
        {selected ? (
          <CheckRoundedIcon sx={{ fontSize: 18, color: "#fff" }} />
        ) : (
          <PublicRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: selected ? 600 : 500,
          fontSize: "0.8rem",
          color: selected ? "primary.main" : "text.primary",
          lineHeight: 1.3,
          transition: "color 0.2s",
          letterSpacing: "-0.01em",
        }}
      >
        {country.name}
      </Typography>

      {/* Selected dot indicator */}
      <AnimatePresence>
        {selected && (
          <MotionBox
            key="dot"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 22 }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: "primary.main",
            }}
          />
        )}
      </AnimatePresence>
    </MotionBox>
  </Grid>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const CountryModal = ({ open, onSuccess }) => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const selectedName = countries.find((c) => c.id === selectedCountry)?.name;

  const filtered = countries.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    if (!open) return;
    setSelectedCountry(null);
    setSearch("");
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const data = await getCountries();
        setCountries(data);
      } catch (err) {
        console.error("Failed to fetch countries", err);
        toast.error("Could not load countries. Please try again.");
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, [open]);

  const handleContinue = async () => {
    if (!selectedCountry) {
      toast.error("Please select a country to continue.");
      return;
    }
    try {
      setSaving(true);
      await updateCountry(selectedCountry);
      onSuccess();
    } catch (err) {
      console.error("Backend Error:", err.response?.data);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        component: motion.div,
        variants: dialogVariants,
        initial: "hidden",
        animate: "show",
        exit: "exit",
        elevation: 0,
        sx: {
          borderRadius: "24px",
          border: "1px solid",
          borderColor: "grey.100",
          boxShadow:
            "0 32px 64px -12px rgba(16,24,40,0.14), 0 0 0 1px rgba(16,24,40,0.05)",
          overflow: "hidden",
          bgcolor: "#fff",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(6px)",
            bgcolor: "rgba(15,15,35,0.35)",
          },
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3.5,
          pt: 3.5,
          pb: 2.5,
          borderBottom: "1px solid",
          borderColor: "grey.100",
          background:
            "linear-gradient(135deg, #F8F7FF 0%, #FAFAFF 50%, #F5F7FF 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          {/* Icon badge */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 4px 14px rgba(79,70,229,0.35)",
            }}
          >
            <PublicRoundedIcon sx={{ fontSize: 24, color: "#fff" }} />
          </Box>

          <Box sx={{ flex: 1, pt: 0.3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "1.1rem",
                color: "text.primary",
                letterSpacing: "-0.02em",
                lineHeight: 1.3,
              }}
            >
              Select Your Country
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.4, fontSize: "0.82rem" }}
            >
              We use this to personalise your experience
            </Typography>
          </Box>

          {/* Step indicator */}
          <Chip
            label="Step 1 of 1"
            size="small"
            sx={{
              bgcolor: alpha("#4F46E5", 0.08),
              color: "primary.main",
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 24,
              border: "1px solid",
              borderColor: alpha("#4F46E5", 0.2),
              mt: 0.5,
            }}
          />
        </Box>
      </Box>

      {/* ── Body ───────────────────────────────────────────── */}
      <DialogContent sx={{ p: 3.5, bgcolor: "#fff" }}>
        {/* Search bar */}
        {!loadingCountries && countries.length > 0 && (
          <MotionBox
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08, duration: 0.28 }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.8,
              py: 1,
              mb: 2.5,
              borderRadius: "12px",
              border: "1.5px solid",
              borderColor: "grey.200",
              bgcolor: "#FAFAFA",
              transition: "border-color 0.2s",
              "&:focus-within": {
                borderColor: "primary.main",
                bgcolor: "#fff",
                boxShadow: "0 0 0 3px rgba(79,70,229,0.10)",
              },
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
            <InputBase
              placeholder="Search countries…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                flex: 1,
                fontSize: "0.85rem",
                color: "text.primary",
                "& input::placeholder": { color: "text.disabled" },
              }}
            />
          </MotionBox>
        )}

        {/* Countries grid */}
        {loadingCountries ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 2,
            }}
          >
            <CircularProgress size={36} thickness={3} color="primary" />
            <Typography
              variant="body2"
              sx={{ color: "text.disabled", fontSize: "0.82rem" }}
            >
              Loading countries…
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              pr: 0.5,
              mr: -0.5,
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "grey.200",
                borderRadius: 8,
              },
            }}
          >
            <AnimatePresence mode="wait">
              {filtered.length > 0 ? (
                <MotionGrid
                  key="grid"
                  container
                  spacing={1.2}
                  component={motion.div}
                  variants={containerVariants}
                  initial="hidden"
                  animate="show"
                >
                  {filtered.map((country) => (
                    <CountryTile
                      key={country.id}
                      country={country}
                      selected={selectedCountry === country.id}
                      onClick={setSelectedCountry}
                    />
                  ))}
                </MotionGrid>
              ) : (
                <MotionBox
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  sx={{
                    textAlign: "center",
                    py: 5,
                    color: "text.disabled",
                  }}
                >
                  <PublicRoundedIcon
                    sx={{ fontSize: 36, mb: 1, opacity: 0.4 }}
                  />
                  <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                    No countries match &ldquo;{search}&rdquo;
                  </Typography>
                </MotionBox>
              )}
            </AnimatePresence>
          </Box>
        )}

        {/* ── Footer ─────────────────────────────────────── */}
        <Box
          sx={{
            mt: 3,
            pt: 2.5,
            borderTop: "1px solid",
            borderColor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Selection summary */}
          <AnimatePresence mode="wait">
            {selectedCountry ? (
              <MotionBox
                key="selected"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2 }}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontSize: "0.82rem" }}
                >
                  Selected:{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {selectedName}
                  </Box>
                </Typography>
              </MotionBox>
            ) : (
              <MotionBox
                key="none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "text.disabled", fontSize: "0.82rem" }}
                >
                  No country selected yet
                </Typography>
              </MotionBox>
            )}
          </AnimatePresence>

          {/* CTA button */}
          <MotionBox
            whileHover={selectedCountry && !saving ? { scale: 1.03 } : {}}
            whileTap={selectedCountry && !saving ? { scale: 0.97 } : {}}
            transition={{ duration: 0.15 }}
          >
            <Button
              variant="contained"
              disabled={!selectedCountry || saving}
              onClick={handleContinue}
              disableElevation
              endIcon={
                saving ? null : (
                  <ArrowForwardRoundedIcon
                    sx={{ fontSize: "18px !important" }}
                  />
                )
              }
              sx={{
                px: 3.5,
                py: 1.2,
                borderRadius: "12px",
                fontWeight: 600,
                fontSize: "0.88rem",
                letterSpacing: "-0.01em",
                textTransform: "none",
                bgcolor: "primary.main",
                minWidth: 140,
                boxShadow: selectedCountry
                  ? "0 4px 14px rgba(79,70,229,0.35)"
                  : "none",
                transition:
                  "background-color 0.2s, box-shadow 0.2s, opacity 0.2s",
                "&:hover": {
                  bgcolor: "primary.dark",
                  boxShadow: "0 6px 20px rgba(79,70,229,0.4)",
                },
                "&.Mui-disabled": {
                  bgcolor: "grey.100",
                  color: "text.disabled",
                  boxShadow: "none",
                },
              }}
            >
              {saving ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} thickness={3} color="inherit" />
                  <span>Saving…</span>
                </Box>
              ) : (
                "Continue"
              )}
            </Button>
          </MotionBox>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
